import React from "react";
import { MdSettings } from "react-icons/md";
import { LLM_MODEL_TYPE_CONFIGS } from "constants/configuration-models";
import { useTranslation } from "react-i18next";
import { validateJson } from "utils";
import PureDropDown from "components/pure-dropdown";

export interface LLMModelFormData {
  name: string;
  type: string;
  configuration: string;
}

interface LLMModelFormFieldsProps {
  formData: LLMModelFormData;
  onFormDataChange: (data: LLMModelFormData) => void;
  showValidation?: boolean;
  className?: string;
}

const LLMModelFormFields: React.FC<LLMModelFormFieldsProps> = ({
  formData,
  onFormDataChange,
  showValidation = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const modelTypeConfigs = LLM_MODEL_TYPE_CONFIGS;
  const modelTypes = Object.keys(modelTypeConfigs);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({
      ...formData,
      name: e.target.value,
    });
  };

  const handleTypeChange = (value: string | string[]) => {
    const newType = typeof value === 'string' ? value : value[0] || formData.type;
    onFormDataChange({
      ...formData,
      type: newType,
      configuration: JSON.stringify(modelTypeConfigs[newType], null, 2),
    });
  };

  const handleConfigurationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onFormDataChange({
      ...formData,
      configuration: e.target.value,
    });
  };

  const configurationError = showValidation ? validateJson(formData.configuration) : "";

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Model Name */}
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("settings.modelModal.llmModelName")}
          <span className="ml-1 text-red-500 dark:text-red-400">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={handleNameChange}
          placeholder={t("settings.modelModal.enterLLMName")}
          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400"
        />
      </div>

      {/* Model Type */}
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("settings.modelModal.llmModelType")}
          <span className="ml-1 text-red-500 dark:text-red-400">*</span>
        </label>
        <PureDropDown
          placeholder={t("settings.modelModal.llmModelType")}
          options={modelTypes.map((type) => ({ value: type, label: type }))}
          value={formData.type}
          onChange={handleTypeChange}
          multiple={false}
          showClearButton={false}
        />
      </div>

      {/* Configuration */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <MdSettings className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            {t("settings.modelModal.llmModelConfiguration")}
          </label>
        </div>
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          {t("settings.modelModal.configurationJsonType")}
        </p>
        <textarea
          value={formData.configuration}
          onChange={handleConfigurationChange}
          placeholder={t("settings.modelModal.enterJsonConfiguration")}
          rows={8}
          className={`block w-full rounded-lg border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
            configurationError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/10'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400'
          }`}
        />
        {configurationError && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {configurationError}
          </div>
        )}
      </div>
    </div>
  );
};

export default LLMModelFormFields;

